"use client";

// PASTE #246: client-side "is this user approved?" for UX gating of
// prices + authenticated nav. Reads the mag-portal-access cookie and
// checks for the approved/admin group (base64 decode - NOT crypto; the
// authoritative gate is the server SSR (isApprovedRequest) + the /api
// routes). Re-reads on Amplify auth events so it flips on sign-in/out.
//
// A signed-in-but-not-approved user (e.g. a fresh federated login) is
// "not" here, so usePriceGate hides prices and the marketing nav shows
// the Apply CTA instead of Dashboard.
//
// MKT-#10: dropped the aws-amplify Hub listener (it pulled the Amplify
// bundle into every marketing page). The value already comes from the
// mag-portal-access cookie; we re-read on focus/visibility instead, since
// sign-in/out on these surfaces happens via navigation (which reloads).

import { useEffect, useState } from "react";
import { canSeePricesFromAccessToken } from "@/lib/auth/groups";

export type ApprovalState = "loading" | "approved" | "not";

const ACCESS_COOKIE = "mag-portal-access";

function readAccessCookie(): string | null {
  if (typeof document === "undefined") return null;
  for (const raw of document.cookie.split(";")) {
    const trimmed = raw.trim();
    if (trimmed.startsWith(`${ACCESS_COOKIE}=`)) {
      return decodeURIComponent(trimmed.slice(ACCESS_COOKIE.length + 1));
    }
  }
  return null;
}

export function useIsApproved(): ApprovalState {
  const [state, setState] = useState<ApprovalState>("loading");

  useEffect(() => {
    let cancelled = false;
    const recheck = () => {
      if (cancelled) return;
      const approved = canSeePricesFromAccessToken(readAccessCookie());
      setState(approved ? "approved" : "not");
    };
    recheck();
    // Re-read when the tab regains focus / becomes visible (covers a
    // sign-in or sign-out that happened via navigation or in another tab).
    window.addEventListener("focus", recheck);
    document.addEventListener("visibilitychange", recheck);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", recheck);
      document.removeEventListener("visibilitychange", recheck);
    };
  }, []);

  return state;
}
